import React, { useState, useMemo } from "react";
import { useTheme } from "@emotion/react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from "@mui/material";
import useStyles from "./styles";

export default function CustomTableComponent({
  rows = [],
  columns = [],
  sxDatagrid = {},
  title,
}) {
  const theme = useTheme();
  const styles = useStyles(theme);

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    if (!orderBy) return rows;
    const col = columns.find((c) => c.field === orderBy);

    return [...rows].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (col?.valueGetter) {
        aValue = col.valueGetter(aValue, a);
        bValue = col.valueGetter(bValue, b);
      }

      if (aValue < bValue) {
        return order === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [rows, order, orderBy, columns]);

  return (
    <Box sx={{ width: "100%", height: "100%", ...sxDatagrid }}>
      {title && (
        <Typography sx={{ color: theme.palette.text.primary, fontWeight: 500, p: 2 }}>
          {title}
        </Typography>
      )}
      <TableContainer sx={styles.tableContainer} component={Paper} elevation={0}>
        <Table sx={styles.table} stickyHeader size="small">
          <TableHead sx={styles.tableHead}>
            <TableRow>
              {columns.map((headCell) => {
                const align = headCell.align || (headCell.type === "number" ? "right" : "left");
                return (
                  <TableCell
                    key={headCell.field}
                    align={align}
                    sx={{
                      ...styles.tableHeadCell,
                      width: headCell.width || "auto",
                      minWidth: headCell.minWidth || "auto",
                    }}
                    sortDirection={orderBy === headCell.field ? order : false}
                  >
                    {headCell.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === headCell.field}
                        direction={orderBy === headCell.field ? order : "asc"}
                        onClick={() => handleRequestSort(headCell.field)}
                        sx={styles.sortLabel}
                      >
                        {headCell.headerName || headCell.field}
                      </TableSortLabel>
                    ) : (
                      headCell.headerName || headCell.field
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((row, index) => {
              const rowClass = index % 2 === 1 ? "linha-impar" : "linha-par";
              return (
                <TableRow
                  hover
                  key={row.id ?? index}
                  className={rowClass}
                  sx={styles.tableRow}
                >
                  {columns.map((col) => {
                    const align = col.align || (col.type === "number" ? "right" : "left");
                    
                    let content = row[col.field];
                    
                    if (col.valueGetter) {
                      content = col.valueGetter(content, row);
                    }
                    if (col.valueFormatter) {
                      content = col.valueFormatter(content, row);
                    }
                    if (col.renderCell) {
                      // Mock DataGrid params object
                      content = col.renderCell({ row, value: row[col.field] });
                    }

                    return (
                      <TableCell key={col.field} align={align} sx={styles.tableCell}>
                        {content}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            {sortedRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={styles.tableCell}>
                  Nenhum registro encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
